// FoundHerBadgeAdmin.jsx
//
// Visible to any logged-in user by design — no role gate. Visibility is
// scoped server-side: badgeAdminList only ever returns requests where
// institutionCode === the logged-in user's own think5whyscode, so anyone
// who isn't actually a college/badge admin just sees an empty list.

import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faClock, faSave, faTimesCircle, faGraduationCap } from "@fortawesome/free-solid-svg-icons";
import useFetch from "../../../../hooks/useFetch";

const BADGE_LIST_ENDPOINT = "/foundher/badge/admin/list";
const BADGE_UPDATE_ENDPOINT = (id) => `/foundher/badge/admin/${id}`;

const STATUS_STYLES = {
  Pending: "text-amber-400",
  Verified: "text-green-400",
  Rejected: "text-red-400",
  Expired: "text-zinc-400",
};

const emptyDraft = {
  verificationStatus: "Pending",
  badgeTitle: "",
  badgeColor: "#EC4899",
  badgeIcon: "faGraduationCap",
  remarks: "",
  validTill: "",
  active: true,
  institutionName: "",
  institutionType: "College",
  city: "",
  state: "",
  website: "",
};

export default function FoundHerBadgeAdmin() {
  const authState = useSelector((state) => state.authReducer);
  const token = authState?.token;
  const myCode = authState?.user?.think5whyscode;
  const [fetchData] = useFetch();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("Pending"); // "" = all

  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [saveStatus, setSaveStatus] = useState(""); // "", "saving", "saved", "error"
  const [saveError, setSaveError] = useState("");

  const loadRequests = async () => {
    setLoading(true);
    try {
      const query = filter ? `?verificationStatus=${filter}` : "";
      const config = {
        url: `${BADGE_LIST_ENDPOINT}${query}`,
        method: "get",
        headers: { Authorization: token },
      };
      const response = await fetchData(config, { silent: true });
      if (response?.status) {
        setRequests(response.records || []);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const openRequest = (req) => {
    setSelected(req);
    setDraft({
      verificationStatus: req.verificationStatus || "Pending",
      badgeTitle: req.badgeTitle || "",
      badgeColor: req.badgeColor || "#EC4899",
      badgeIcon: req.badgeIcon || "faGraduationCap",
      remarks: req.remarks || "",
      validTill: req.validTill ? req.validTill.slice(0, 10) : "",
      active: req.active !== undefined ? req.active : true,
      institutionName: req.institutionName || "",
      institutionType: req.institutionType || "College",
      city: req.city || "",
      state: req.state || "",
      website: req.website || "",
    });
    setSaveStatus("");
    setSaveError("");
  };

  const handleDraftChange = (e) => {
    const { name, value, type, checked } = e.target;
    setDraft((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSave = async (statusOverride) => {
    if (!selected) return;

    const payload = { ...draft, ...(statusOverride && { verificationStatus: statusOverride }) };

    setSaveStatus("saving");
    setSaveError("");
    try {
      const config = {
        url: BADGE_UPDATE_ENDPOINT(selected._id || selected.id),
        method: "put",
        headers: { Authorization: token },
        data: payload,
      };
      const response = await fetchData(config, { silent: true });
      if (response?.status) {
        setSaveStatus("saved");
        setDraft((prev) => ({ ...prev, verificationStatus: payload.verificationStatus }));
        loadRequests();
      } else {
        setSaveStatus("error");
        setSaveError(response?.message || "Save failed.");
      }
    } catch (err) {
      console.log(err);
      setSaveStatus("error");
      setSaveError("Save failed.");
    }
  };

  const initials = (name) =>
    (name || "?")
      .split(" ")
      .map((p) => p[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  return (
    <div className="min-h-screen bg-zinc-900 text-white p-4 md:p-8">
      <h1 className="text-2xl font-bold bg-gradient-to-r from-pink-400 to-violet-400 bg-clip-text text-transparent mb-1">
        FoundHer College Badge — Admin
      </h1>
      <p className="text-sm text-gray-200 mb-1">
        Requests directed to your institution code{myCode ? ` (${myCode})` : ""}.
      </p>
      {!myCode && (
        <p className="text-xs text-amber-400 mb-6">
          No institution code found on your account — you won't see any requests until one is set.
        </p>
      )}
      <div className="mb-6" />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Requests list */}
        <div className="lg:col-span-2 bg-zinc-800 rounded-2xl shadow p-4">
          <div className="flex gap-2 mb-4 flex-wrap">
            {["Pending", "Verified", "Rejected", "Expired", ""].map((f) => (
              <button
                key={f || "all"}
                onClick={() => setFilter(f)}
                className={`w-fit px-2 py-1 rounded-full text-xs font-medium capitalize ${
                  filter === f ? "bg-violet-600 text-white" : "bg-zinc-700 text-zinc-300 hover:bg-zinc-600"
                }`}
              >
                {f || "All"}
              </button>
            ))}
          </div>

          {loading ? (
            <p className="text-sm text-gray-100 py-6 text-center">Loading requests...</p>
          ) : requests.length === 0 ? (
            <p className="text-sm text-gray-100 py-6 text-center">No requests in this view.</p>
          ) : (
            <div className="space-y-2 max-h-[70vh] overflow-y-auto">
              {requests.map((req) => (
                <button
                  key={req._id || req.id}
                  onClick={() => openRequest(req)}
                  className={`w-full text-left p-3 rounded-xl border transition ${
                    selected && (selected._id || selected.id) === (req._id || req.id)
                      ? "border-violet-400 bg-violet-900/40"
                      : "border-zinc-700 bg-zinc-700/60 hover:border-violet-500"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-pink-600 flex items-center justify-center text-xs font-bold shrink-0">
                      {initials(req.founderProfile?.userName || req.founderName)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-sm text-gray-300 truncate">
                          {req.founderProfile?.userName || req.founderName}
                        </span>
                        <span className={`flex items-center gap-1 text-xs shrink-0 ${STATUS_STYLES[req.verificationStatus] || "text-zinc-400"}`}>
                          {req.verificationStatus === "Verified" ? (
                            <FontAwesomeIcon icon={faCheckCircle} />
                          ) : req.verificationStatus === "Rejected" ? (
                            <FontAwesomeIcon icon={faTimesCircle} />
                          ) : (
                            <FontAwesomeIcon icon={faClock} />
                          )}
                          {req.verificationStatus}
                        </span>
                      </div>
                      <p className="text-xs text-gray-200 truncate">
                        {req.founderProfile?.headline || "—"}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Editor */}
        <div className="lg:col-span-3 bg-zinc-800 rounded-2xl shadow p-6">
          {!selected ? (
            <p className="text-sm text-gray-100 py-10 text-center">
              Select a request on the left to review it.
            </p>
          ) : (
            <>
              <div className="mb-4 p-4 bg-zinc-900/60 border border-zinc-700 rounded-xl flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-pink-600 flex items-center justify-center text-lg font-bold shrink-0">
                  {initials(selected.founderProfile?.userName || selected.founderName)}
                </div>
                <div>
                  <p className="font-semibold text-white">
                    {selected.founderProfile?.userName || selected.founderName}
                  </p>
                  <p className="text-xs text-zinc-400">{selected.founderCode}</p>
                  {selected.founderProfile?.headline && (
                    <p className="text-sm text-zinc-300 mt-1">{selected.founderProfile.headline}</p>
                  )}
                  {selected.founderProfile?.location && (
                    <p className="text-xs text-zinc-400 mt-1">{selected.founderProfile.location}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <input
                  name="institutionName"
                  value={draft.institutionName}
                  onChange={handleDraftChange}
                  placeholder="Institution name"
                  className="border border-zinc-700 bg-zinc-900 text-white placeholder-zinc-500 rounded-lg px-3 py-2 text-sm focus:border-violet-400 focus:outline-none"
                />
                <select
                  name="institutionType"
                  value={draft.institutionType}
                  onChange={handleDraftChange}
                  className="border border-zinc-700 bg-zinc-900 text-white rounded-lg px-3 py-1 text-sm focus:border-violet-400 focus:outline-none"
                >
                 {["College", "University", "School", "NGO", "Community", "Corporate", "Religious Trust", "SHG", "Women Cell", "Incubation Centre", "Startup Cell", "Skill Development Centre", "Other"].map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <input
                  name="city"
                  value={draft.city}
                  onChange={handleDraftChange}
                  placeholder="City"
                  className="border border-zinc-700 bg-zinc-900 text-white placeholder-zinc-500 rounded-lg px-3 py-2 text-sm focus:border-violet-400 focus:outline-none"
                />
                <input
                  name="state"
                  value={draft.state}
                  onChange={handleDraftChange}
                  placeholder="State"
                  className="border border-zinc-700 bg-zinc-900 text-white placeholder-zinc-500 rounded-lg px-3 py-2 text-sm focus:border-violet-400 focus:outline-none"
                />
                <input
                  name="website"
                  value={draft.website}
                  onChange={handleDraftChange}
                  placeholder="Website"
                  className="border border-zinc-700 bg-zinc-900 text-white placeholder-zinc-500 rounded-lg px-3 py-2 text-sm sm:col-span-2 focus:border-violet-400 focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <input
                  name="badgeTitle"
                  value={draft.badgeTitle}
                  onChange={handleDraftChange}
                  placeholder="Badge title (e.g. Verified Founder)"
                  className="border border-zinc-700 bg-zinc-900 text-white placeholder-zinc-500 rounded-lg px-3 py-2 text-sm focus:border-violet-400 focus:outline-none"
                />
                <div className="flex items-center gap-2 border border-zinc-700 bg-zinc-900 rounded-lg px-3 py-2">
                  <input
                    type="color"
                    name="badgeColor"
                    value={draft.badgeColor}
                    onChange={handleDraftChange}
                    className="w-8 h-8 bg-transparent border-0 p-0"
                  />
                  <span className="text-xs text-zinc-400 font-mono">{draft.badgeColor}</span>
                </div>
                <input
                  type="date"
                  name="validTill"
                  value={draft.validTill}
                  onChange={handleDraftChange}
                  className="border border-zinc-700 bg-zinc-900 text-white rounded-lg px-3 py-2 text-sm focus:border-violet-400 focus:outline-none"
                />
                <div
                  className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium justify-center"
                  style={{ backgroundColor: draft.badgeColor + "22", color: draft.badgeColor }}
                >
                  <FontAwesomeIcon icon={faGraduationCap} />
                  {draft.badgeTitle || "Badge preview"}
                </div>
              </div>

              <textarea
                name="remarks"
                value={draft.remarks}
                onChange={handleDraftChange}
                placeholder="Internal remarks (visible to your team only)"
                style={{height: "100px"}}
                className="w-full border border-zinc-700 bg-zinc-900 text-white placeholder-zinc-500 rounded-lg px-3 py-2 text-sm h-20 resize-y focus:border-violet-400 focus:outline-none"
              />

              <label className="flex items-center gap-2 mt-4 text-sm text-zinc-300">
                <input
                  type="checkbox"
                  name="active"
                  checked={draft.active}
                  onChange={handleDraftChange}
                />
                Active
              </label>

              {saveStatus === "error" && <p className="text-xs text-red-400 mt-2">{saveError}</p>}
              {saveStatus === "saved" && <p className="text-xs text-green-400 mt-2">Saved.</p>}

              <div className="flex gap-3 mt-5">
                <button
                  onClick={() => handleSave(null)}
                  disabled={saveStatus === "saving"}
                  className="flex-1 flex items-center justify-center gap-2 w-fit px-2 py-1 rounded-xl border border-violet-500 text-violet-300 hover:bg-violet-900/40 text-sm font-medium disabled:opacity-60"
                >
                  <FontAwesomeIcon icon={faSave} /> Save
                </button>
                <button
                  onClick={() => handleSave("Rejected")}
                  disabled={saveStatus === "saving"}
                  className="flex-1 flex items-center justify-center gap-2 w-fit px-2 py-1 rounded-xl border border-red-500 text-red-300 hover:bg-red-900/40 text-sm font-medium disabled:opacity-60"
                >
                  <FontAwesomeIcon icon={faTimesCircle} /> Reject
                </button>
                <button
                  onClick={() => handleSave("Verified")}
                  disabled={saveStatus === "saving"}
                  className="flex-1 flex items-center justify-center gap-2 w-fit px-2 py-1 rounded-xl bg-gradient-to-r from-pink-500 to-violet-500 text-white text-sm font-medium hover:brightness-110 disabled:opacity-60"
                >
                  <FontAwesomeIcon icon={faCheckCircle} /> Verify & Activate
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}